// ONLY 1 CONTROLLER AND 1 MODEL.  MULTIPLE VIEWS SINCE THEY ARE MUCH BIGGER.

import { API_URL, RESULTS_PER_PAGE, KEY } from './config.js';
import { AJAX } from './helpers.js';

// create two empty objects.  These can now be used in the controller.  When state gets updated by loadRecipe, then state is updated in the controller which imports the state.
// export so you can use it in the controller.
export const state = {
  recipe: {},
  search: {
    query: '',
    results: [],
    page: 1,
    resultsPerPage: RESULTS_PER_PAGE,
  },
  bookmarks: [],
};

const createRecipeObject = function (data) {
  const { recipe } = data.data;
  return {
    id: recipe.id,
    title: recipe.title,
    publisher: recipe.publisher,
    sourceUrl: recipe.source_url,
    image: recipe.image_url,
    servings: recipe.servings,
    cookingTime: recipe.cooking_time,
    ingredients: recipe.ingredients,
    // there might not always be a key in recipes, so use the && operator (to short-circuit.  If it doesn't exist, nothing happens) with the spread operator.  What's happening here is if there is a key, then return the key object that returns.  So the entire expression becomes that object, and then spread to put the values here.  Basically, this is a way to conditionally add properties to an object
    ...(recipe.key && { key: recipe.key }),
  };
};

// needs to be exported for use in the controller.  This is responsible for fetching the data from the Forkify API.  It is an async function.
// pass in (id) to this function.  loadRecipe will NOT return anything; it just changes the state object.  So we're not storing any results into a variable.  But, we get access to state.recipe.
// Then, the controller gets the recipe out of it.
// remember that since it's an async function, it returns a PROMISE and needs to be handled to get results out of it or stop the execution in the function that's calling the other async function!  So we have to await that promise.  Basically, it's one async function calling another async function.
// loadRecipe function calls another async function, getJSON, and getJSON returns the data variable.  "data" is the resolved value of the promise that getJSON returns.

export const loadRecipe = async function (id) {
  try {
    const data = await AJAX(`${API_URL}${id}?key=${KEY}`);
    /* let { recipe } = data.data;
    state.recipe = {
      id: recipe.id,
      title: recipe.title,
      publisher: recipe.publisher,
      sourceUrl: recipe.source_url,
      image: recipe.image_url,
      servings: recipe.servings,
      cookingTime: recipe.cooking_time,
      ingredients: recipe.ingredients,
    };
    */
    state.recipe = createRecipeObject(data);
    // if ANY bookmark has a matching id, we want the current recipe to be bookmarked
    if (state.bookmarks.some(bookmark => bookmark.id === id))
      state.recipe.bookmarked = true;
    else state.recipe.bookmarked = false;
    console.log(state.recipe);
  } catch (err) {
    // the error occurs in the getJSON function, and not in here.  How is it done?  with "throw".  But this really should be in the view, so move that logic over to recipeView.
    console.error(`${err} 💥💥💥💥`);
    throw err;
  }
};

// export so it can be used by the controller.  The controller will tell it what to search for
export const loadSearchResults = async function (query) {
  try {
    state.search.query = query;
    const data = await AJAX(`${API_URL}?search=${query}&key=${KEY}`);
    console.log(data);
    // create a new array, loop over it
    state.search.results = data.data.recipes.map(recipe => {
      return {
        id: recipe.id,
        title: recipe.title,
        publisher: recipe.publisher,
        image: recipe.image_url,
        ...(recipe.key && { key: recipe.key }),
      }; // store this in state
    });
    state.search.page = 1;
  } catch (err) {
    console.error(`${err} 💥💥💥💥`);
    throw err;
  }
};

// this will paginate the search results.  not an async function since the search results are already loaded.  We want to go into the state and get the data for the requested page (basically, return a portion of the results)
export const getSearchResultsPage = function (page = state.search.page) {
  state.search.page = page;
  // slice will return part of it, so from result 1 to 10
  const start = (page - 1) * state.search.resultsPerPage; // if we returned 9 pages, then 9 - 1 = 8.  8 * 10 = 80
  const end = page * state.search.resultsPerPage; // if we returned 9 pages, then 9 * 10 = 90

  return state.search.results.slice(start, end);
};

export const updateServings = function (newServings) {
  // this goes into the recipe ingredients and changes the quantity of ingredients
  state.recipe.ingredients.forEach(ingredient => {
    ingredient.quantity =
      (ingredient.quantity * newServings) / state.recipe.servings;
  });
  state.recipe.servings = newServings;
};

// no need to export since it will be called in addBookmark and deleteBookmark
const persistBookmarks = function () {
  // set a string by calling JSON.stringify and convert state.bookmarks to a string
  localStorage.setItem('bookmarks', JSON.stringify(state.bookmarks));
};

export const addBookmark = function (recipe) {
  state.bookmarks.push(recipe);

  // mark the current recipe as bookmarked
  if (recipe.id === state.recipe.id) state.recipe.bookmarked = true;

  persistBookmarks();
};

export const deleteBookmark = function (id) {
  // calculate index with findIndex.  We're looking for the element with the id that equals the id that was passed in (only 1 can exist).  Then take the index and delete from the array
  const index = state.bookmarks.findIndex(el => el.id === id);
  // splice will delete the id.  Index of the location of the element, deleting only 1
  state.bookmarks.splice(index, 1);

  // current recipe is no longer a bookmark
  if (id === state.recipe.id) state.recipe.bookmarked = false;

  persistBookmarks();
};
const init = function () {
  // call init to get the bookmarks that are persisted in localStorage
  const storage = localStorage.getItem('bookmarks');
  // JSON.parse will convert STRING back to OBJECT.
  if (storage) state.bookmarks = JSON.parse(storage);
};
init();

// this will make a request to the API (so it will be an async function) and will get data for the new recipe
export const uploadRecipe = async function (newRecipe) {
  try {
    // get the raw input data and transform it into the same format as the data we get out of the API.

    // create an array of ingredients, so use the map methods to create new arrays.  We need an array, so convert the object we get back into an array.  This is the opposite of Object.fromEntries
    const ingredients = Object.entries(newRecipe)
      // filter with the entry.  We only want the entries where the first element of the array starts with "ingredient".  The second part cannot be empty.  In the entry, the 1st element of the current entry should start with ingredient and the 2nd one should exist.
      /* 
    it will say something like ingredient-1, 0.5,kg,rice
    */
      .filter(entry => entry[0].startsWith('ingredient') && entry[1] !== '')
      // use map to put into an object. so take the ingredients and split by a comma, removing any whitespace
      .map(ingredient => {
        // split the string into multiple parts that returns an array.  Then loop over the array and trim the elements
        const ingredientsArray = ingredient[1].split(', ').map(el => el.trim());
        // does ingredientsArray have a length of 3?  If not, throw an error
        if (ingredientsArray.length !== 3)
          throw new Error('Wrong ingredient format.');
        //de-struct the array
        const [quantity, unit, description] = ingredientsArray;

        return {
          // quantity: 1, unit: "", description: "chopped sweet onion"
          // convert null to a number with +quantity.  If there is a quantity, convert to a number with the ternaray operator.  If there is an empty string, return null
          quantity: quantity ? +quantity : null,
          unit,
          description,
        };
      });

    const recipe = {
      title: newRecipe.title,
      source_url: newRecipe.sourceUrl,
      image_url: newRecipe.image,
      publisher: newRecipe.publisher,
      // force these to be converted to numbers with +
      cooking_time: +newRecipe.cookingTime,
      servings: +newRecipe.servings,
      ingredients,
    };

    const data = await AJAX(`${API_URL}?key=${KEY}`, recipe);
    state.recipe = createRecipeObject(data);
    addBookmark(state.recipe);
  } catch (err) {
    throw err;
  }
};
// some ideas to put into this web app:  1) display the number of total recipes found, 2) do the ingredient validation before submitting the form, 3) IMPROVE the recipe input (change the way users can add their ingredients), 4) weekly meal-planning feature (assign recipes for the week and show them on a weekly calendar), 5) get nutrition data?
